import React, { useState, useEffect } from 'react';
import { Server, Clock, AlertCircle, CheckCircle, Shield, FileText, LogIn, LayoutDashboard } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import CodeRain from './CodeRain';
import TermsOfService from './TermsOfService';
import PrivacyPolicy from './PrivacyPolicy';
import './HomePage.css';

function HomePage({ onShowLogin, isAuthenticated, onGoToDashboard }) {
  const [maintenanceData, setMaintenanceData] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState('home'); // 'home', 'terms', 'privacy'

  useEffect(() => {
    fetchMaintenanceData();
    const interval = setInterval(fetchMaintenanceData, 5000); // Refresh every 5 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchMaintenanceData = async () => {
    try {
      const response = await fetch('/api/maintenance');
      const data = await response.json();
      setMaintenanceData(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching maintenance data:', error);
      setLoading(false);
    }
  };

  const calculateProgress = (startTime, estimatedEndTime) => {
    if (!startTime || !estimatedEndTime) return 0;
    
    const start = new Date(startTime).getTime();
    const end = new Date(estimatedEndTime).getTime();
    const now = Date.now();
    
    if (now < start) return 0;
    if (now > end) return 100;
    
    const total = end - start;
    const elapsed = now - start;
    return Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'Non défini';
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const serversInMaintenance = Object.entries(maintenanceData).filter(
    ([_, data]) => data.inMaintenance
  );

  const allServers = Object.entries(maintenanceData);

  return (
    <div className="home-page">
      <CodeRain />
      <div className="home-header">
        <img src="/logo.png" alt="Logo" className="home-logo" />
        <div className="home-title">
          <h1>Discord Maintenance</h1>
          <p>Suivi en temps réel des maintenances de serveurs</p>
        </div>
        {isAuthenticated ? (
          <button className="dashboard-header-btn" onClick={onGoToDashboard}>
            <LayoutDashboard size={20} />
            Dashboard
          </button>
        ) : (
          <button className="login-header-btn" onClick={onShowLogin}>
            <LogIn size={20} />
            Connexion
          </button>
        )}
      </div>

      {currentView === 'home' && (
        <>
          <div className="home-stats">
            <div className="stat-card">
              <Server className="stat-icon" />
              <div className="stat-info">
                <span className="stat-value">{allServers.length}</span>
                <span className="stat-label">Serveurs surveillés</span>
              </div>
            </div>
            <div className="stat-card stat-card-warning">
              <AlertCircle className="stat-icon" />
              <div className="stat-info">
                <span className="stat-value">{serversInMaintenance.length}</span>
                <span className="stat-label">En maintenance</span>
              </div>
            </div>
            <div className="stat-card stat-card-success">
              <CheckCircle className="stat-icon" />
              <div className="stat-info">
                <span className="stat-value">{allServers.length - serversInMaintenance.length}</span>
                <span className="stat-label">Opérationnels</span>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Chargement des données...</p>
            </div>
          ) : (
            <div className="servers-list">
              <h2 className="section-title">
                {serversInMaintenance.length > 0 
                  ? `Serveurs en maintenance (${serversInMaintenance.length})`
                  : 'Tous les serveurs sont opérationnels'}
              </h2>

              {serversInMaintenance.length === 0 && allServers.length === 0 ? (
                <div className="empty-state">
                  <Server className="empty-icon" />
                  <p>Aucun serveur surveillé pour le moment</p>
                </div>
              ) : serversInMaintenance.length === 0 ? (
                <div className="empty-state success">
                  <CheckCircle className="empty-icon" />
                  <p>Tous les systèmes fonctionnent normalement</p>
                </div>
              ) : (
                <div className="maintenance-cards">
                  {serversInMaintenance.map(([guildId, data]) => {
                    const progress = calculateProgress(data.startTime, data.estimatedEndTime);
                    return (
                      <div key={guildId} className="maintenance-card">
                        <div className="card-header">
                          <div className="server-info">
                            {data.icon && (
                              <img 
                                src={data.icon} 
                                alt="Server Icon" 
                                className="server-icon"
                              />
                            )}
                            <div>
                              <h3>{data.name || 'Serveur inconnu'}</h3>
                              <span className="server-id">ID: {guildId}</span>
                            </div>
                          </div>
                          <div className="status-badge status-maintenance">
                            <AlertCircle size={16} />
                            En maintenance
                          </div>
                        </div>

                        <div className="card-content">
                          <div className="message-section">
                            <div className="message-label">
                              <AlertCircle size={16} />
                              Message de maintenance
                            </div>
                            <div className="maintenance-message">
                              <ReactMarkdown>{data.maintenanceMessage || 'Aucun message de maintenance'}</ReactMarkdown>
                            </div>
                          </div>

                          <div className="time-section">
                            <div className="time-item">
                              <Clock size={16} />
                              <div>
                                <span className="time-label">Début:</span>
                                <span className="time-value">{formatTime(data.startTime)}</span>
                              </div>
                            </div>
                            <div className="time-item">
                              <Clock size={16} />
                              <div>
                                <span className="time-label">Fin estimée:</span>
                                <span className="time-value">{formatTime(data.estimatedEndTime)}</span>
                              </div>
                            </div>
                          </div>

                          <div className="progress-section">
                            <div className="progress-header">
                              <span className="progress-label">Progression</span>
                              <span className="progress-value">{progress}%</span>
                            </div>
                            <div className="progress-bar">
                              <div 
                                className="progress-fill" 
                                style={{ width: `${progress}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {allServers.length > serversInMaintenance.length && (
                <>
                  <h2 className="section-title section-secondary">
                    Serveurs opérationnels ({allServers.length - serversInMaintenance.length})
                  </h2>
                  <div className="operational-cards">
                    {allServers
                      .filter(([_, data]) => !data.inMaintenance)
                      .map(([guildId, data]) => (
                        <div key={guildId} className="operational-card">
                          <div className="card-header">
                            <div className="server-info">
                              {data.icon && (
                                <img 
                                  src={data.icon} 
                                  alt="Server Icon" 
                                  className="server-icon"
                                />
                              )}
                              <div>
                                <h3>{data.name || 'Serveur inconnu'}</h3>
                                <span className="server-id">ID: {guildId}</span>
                              </div>
                            </div>
                            <div className="status-badge status-operational">
                              <CheckCircle size={16} />
                              Opérationnel
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}

      {currentView === 'terms' && <TermsOfService />}
      {currentView === 'privacy' && <PrivacyPolicy />}

      {currentView === 'home' && (
        <div className="home-footer">
          <button 
            className="footer-link" 
            onClick={() => setCurrentView('terms')}
          >
            <FileText size={16} />
            Conditions Générales d'Utilisation
          </button>
          <button 
            className="footer-link" 
            onClick={() => setCurrentView('privacy')}
          >
            <Shield size={16} />
            Politique de Confidentialité
          </button>
        </div>
      )}

      {currentView !== 'home' && (
        <div className="home-footer">
          <button 
            className="footer-link" 
            onClick={() => setCurrentView('home')}
          >
            <Server size={16} />
            Retour à l'accueil
          </button>
        </div>
      )}
    </div>
  );
}

export default HomePage;
